import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
} from "antd";
import { ReloadOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import DataTable from "../../components/common/DataTable";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { PartnerLabel } from "../../components/common/DeletedTag";
import {
  listYetToPay,
  approveTransaction,
  rejectTransaction,
} from "../../services/transactions.service";

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Under Review", value: "underReview" },
  { label: "Hold", value: "hold" },
];

const STATUS_COLORS = {
  pending: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  underReview: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  hold: { color: "#a16207", bg: "#fffbeb", border: "#fde68a" },
};

function StatusBadge({ status, label }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        fontWeight: 600,
      }}
    >
      {label || status}
    </Tag>
  );
}

function formatOmr(v) {
  return Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

export default function YetToPayTab() {
  const { canApprove } = usePermission("transactions");
  const { options: reasonOptions } = useSettingsDropdown("reasons", {
    filter: (item) =>
      !item.applicableModule ||
      item.applicableModule === "transactions" ||
      item.applicableModule === "all",
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, record: null });
  const [rejectForm] = Form.useForm();
  const [approvingId, setApprovingId] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 10,
        search: search || undefined,
        status,
      };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].format("YYYY-MM-DD");
        params.toDate = dateRange[1].format("YYYY-MM-DD");
      }
      const res = await listYetToPay(params);
      setRows(res?.data?.records || []);
      setTotal(res?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load yet-to-pay transactions");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, dateRange]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleApprove = async (record) => {
    setApprovingId(record._id);
    try {
      await approveTransaction(record._id);
      message.success("Transaction approved");
      fetchList();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Approve failed");
    } finally {
      setApprovingId(null);
    }
  };

  const openReject = (record) => {
    rejectForm.resetFields();
    setRejectModal({ open: true, record });
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      setRejecting(true);
      await rejectTransaction(rejectModal.record._id, values);
      message.success("Transaction rejected");
      setRejectModal({ open: false, record: null });
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Reject failed");
    } finally {
      setRejecting(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "transactionDateLabel",
      width: 120,
      render: (v) => v || "—",
    },
    {
      title: "Rider ID",
      dataIndex: "partnerId",
      width: 130,
      render: (v, record) => (
        <PartnerLabel name={v || "—"} deleted={record.riderDeleted} />
      ),
    },
    {
      title: "Rider Name",
      dataIndex: "partnerName",
      render: (v, record) => <PartnerLabel name={v} deleted={record.riderDeleted} />,
    },
    {
      title: "Total Orders",
      dataIndex: "ordersCount",
      width: 110,
      align: "right",
      render: (v) => v ?? 0,
    },
    {
      title: "Total Amount (OMR)",
      dataIndex: "amountCollected",
      align: "right",
      render: formatOmr,
    },
    { title: "Source", dataIndex: "sourceLabel", width: 100, render: (v) => v || "—" },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <StatusBadge status={r.status} label={r.statusLabel} />,
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      align: "right",
      render: (_, record) =>
        canApprove ? (
          <Space>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              loading={approvingId === record._id}
              disabled={Boolean(approvingId) && approvingId !== record._id}
              onClick={() => handleApprove(record)}
            >
              Approve
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              disabled={Boolean(approvingId)}
              onClick={() => openReject(record)}
            >
              Reject
            </Button>
          </Space>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Search rider / order"
          style={{ width: 220 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
        <RangePicker
          value={dateRange}
          onChange={(v) => {
            setPage(1);
            setDateRange(v);
          }}
          format="DD-MM-YYYY"
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 150 }}
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchList}>
          Refresh
        </Button>
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize: 10, total }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="Reject Transaction"
        open={rejectModal.open}
        onCancel={() => setRejectModal({ open: false, record: null })}
        onOk={handleReject}
        confirmLoading={rejecting}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item name="rejectionReason" label="Reason">
            <Select
              allowClear
              options={reasonOptions}
              placeholder="Select reason"
            />
          </Form.Item>
          <Form.Item
            name="rejectionReasonText"
            label="Notes"
            rules={[
              {
                validator: (_, value) => {
                  const reason = rejectForm.getFieldValue("rejectionReason");
                  if (!reason && !value) {
                    return Promise.reject(new Error("Reason or notes required"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea rows={3} placeholder="Rejection notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
